import { z } from 'zod';
import { ZIP_CODE_SCHEMA } from '@/utils/constant';
import { format, startOfDay } from 'date-fns';
import type { UseFormSetValue } from 'react-hook-form';
import type { DdiWorkOrderCreateRequest, DdiWorkOrderUpdateRequest, DdiWorkOrderEditResponse } from '@/types/dueDiligence.types';

/**
 * Single source of truth for DDI-type-specific UI/payload behaviour.
 * Keys match OpenAPI `DdiWorkOrderCreateRequest.ddiType` enum values (and lookup `name`).
 * Prefer this map over scattered string comparisons; typeIds are env-specific UUIDs and
 * cannot be hardcoded.
 */
type DdiTypeBehavior = {
  requiresEntityInquiry?: boolean;
  requiresVin?: boolean;
  /** Defaults to true when omitted */
  addressApplicable?: boolean;
  dueDateOffsetDays: number;
};

export const DDI_TYPE_BEHAVIOR: Record<string, DdiTypeBehavior> = {
  'Affidavit in support of publication': { dueDateOffsetDays: 2 },
  'Corporate DDI': {
    requiresEntityInquiry: true,
    dueDateOffsetDays: 2,
  },
  'DMV Search': { dueDateOffsetDays: 35 },
  'Individual DDI': { dueDateOffsetDays: 2 },
  'Non-military': { dueDateOffsetDays: 2 },
  'Postal inquiry': { dueDateOffsetDays: 30 },
  'SSN search': { dueDateOffsetDays: 2 },
  'Surrogate search': { dueDateOffsetDays: 15 },
  'Vehicle registration': {
    requiresVin: true,
    dueDateOffsetDays: 30,
  },
  'Voter registration': { dueDateOffsetDays: 2 },
  Miscellaneous: { dueDateOffsetDays: 15 },
};

export const DDI_ADDRESS_TYPE_OPTIONS = [
  { label: 'Home Address', value: 'Home Address' },
  { label: 'Business Address', value: 'Business Address' },
];

const getTypeBehavior = (ddiType: string): DdiTypeBehavior | undefined => DDI_TYPE_BEHAVIOR[ddiType];

export const estimateDueDateFromDdiType = (ddiType: string, fromDate = new Date()): Date => {
  const days = getTypeBehavior(ddiType)?.dueDateOffsetDays ?? 2;
  const result = startOfDay(fromDate);
  result.setDate(result.getDate() + days);
  return result;
};

/** Same Day Rush → today; Next Day Rush → tomorrow; otherwise type-based estimate. */
export const estimateDueDateFromRushOrType = (
  ddiType: string,
  options: { sameDayRush?: boolean; nextDayRush?: boolean },
  fromDate = new Date()
): Date => {
  const base = startOfDay(fromDate);
  if (options.sameDayRush) return base;
  if (options.nextDayRush) {
    base.setDate(base.getDate() + 1);
    return base;
  }
  return estimateDueDateFromDdiType(ddiType, fromDate);
};

const optionalTrimmed = z
  .string()
  .nullable()
  .optional()
  .transform((v) => {
    if (v == null) return null;
    const trimmed = v.trim();
    return trimmed.length === 0 ? null : trimmed;
  });

const optionalZip = z
  .string()
  .nullable()
  .optional()
  .transform((v) => {
    if (v == null) return null;
    const trimmed = v.trim();
    return trimmed.length === 0 ? null : trimmed;
  })
  .refine((v) => v == null || ZIP_CODE_SCHEMA.safeParse(v).success, {
    message: 'Zip code is invalid',
  });

const optionalState = z
  .string()
  .nullable()
  .optional()
  .transform((v) => {
    if (v == null) return null;
    const trimmed = v.trim();
    return trimmed.length === 0 ? null : trimmed.toUpperCase();
  })
  .refine((v) => v == null || /^[A-Z]{2}$/.test(v), {
    message: 'State must be a 2-letter code',
  });

/** VIN: 17 chars, A-H J-N P R-Z 0-9 (excludes I, O, Q) when provided */
const VIN_REGEX = /^[A-HJ-NPR-Z0-9]{17}$/i;

export const requiresEntityInquiry = (ddiType: string) =>
  !!getTypeBehavior(ddiType)?.requiresEntityInquiry;

export const requiresVin = (ddiType: string) => !!getTypeBehavior(ddiType)?.requiresVin;

/** Address fields apply unless the type behavior explicitly disables them (default: applicable). */
export const isAddressApplicable = (ddiType: string) =>
  getTypeBehavior(ddiType)?.addressApplicable !== false;

export const requiresAddressType = (ddiType: string) => ddiType === 'Individual DDI';

/** Report dropdown label; Miscellaneous reports display as MISCELLANEOUS. */
export const formatDdiReportOptionLabel = (name: string): string =>
  name === 'Miscellaneous' ? 'MISCELLANEOUS' : name;

export const DdiStep1Schema = z
  .object({
    serviceType: z.literal('Due Diligence'),
    ddiType: z.string({ message: 'DDI Type is required' }).min(1, 'DDI Type is required'),
    ddiTypeId: z.string().optional().nullable(),
    ddiSubtype: optionalTrimmed,
    ddiSubtypeId: z.string().optional().nullable(),
    subtypeRequired: z.boolean().optional().default(false),
    /** True while DDI subtypes are loading for the selected type — blocks Next. */
    subtypesLookupPending: z.boolean().optional().default(false),
    entryDate: z.date({ message: 'Entry Date is required', required_error: 'Entry Date is required' }),
    fileNumber: z
      .string({ message: 'File Number is required' })
      .trim()
      .min(1, 'File Number is required'),
    clientId: z.string().optional().nullable(),
    clientCode: z.string({ message: 'Client Code is required' }).min(1, 'Client Code is required'),
    clientName: z.string().optional().nullable(),
    contactId: z
      .string({ message: "Contact's Name is required" })
      .min(1, "Contact's Name is required")
      .uuid("Contact's Name is required"),
    contactEmail: z.string().optional().nullable(),
    lawyerId: z.preprocess(
      (val) => (val === '' || val == null ? null : val),
      z.string().uuid().nullable().optional()
    ),
    courtName: optionalTrimmed,
    caseType: z.string({ message: 'Case Type is required' }).min(1, 'Case Type is required'),
    caseSubType: z.string({ message: 'Case Sub-Type is required' }).min(1, 'Case Sub-Type is required'),
    caseNumber: z
      .string({ message: 'Case Number is required' })
      .trim()
      .min(1, 'Case Number is required'),
    caseName: z.string({ message: 'Case Name is required' }).trim().min(1, 'Case Name is required'),
  })
  .superRefine((data, ctx) => {
    if (startOfDay(data.entryDate) > startOfDay(new Date())) {
      ctx.addIssue({ code: 'custom', message: 'Entry Date cannot be in the future', path: ['entryDate'] });
    }
    if (data.subtypeRequired && !data.ddiSubtype) {
      ctx.addIssue({ code: 'custom', message: 'DDI Subtype is required', path: ['ddiSubtype'] });
    }
  });

export const DdiStep2Schema = z
  .object({
    ddiType: z.string().optional(),
    personForInquiry: optionalTrimmed,
    entityForInquiry: optionalTrimmed,
    vin: optionalTrimmed,
    addressType: optionalTrimmed,
    addressLine1: optionalTrimmed,
    addressLine2: optionalTrimmed,
    city: optionalTrimmed,
    state: optionalState,
    zipCode: optionalZip,
    showAlternativeAddress: z.boolean().optional().default(false),
    alternativeAddressLine1: optionalTrimmed,
    alternativeAddressLine2: optionalTrimmed,
    alternativeAddressCity: optionalTrimmed,
    alternativeAddressState: optionalState,
    alternativeAddressZipCode: optionalZip,
    notes: z.string().nullable().optional(),
    sameDayRush: z.boolean().optional().default(false),
    nextDayRush: z.boolean().optional().default(false),
    dueDate: z.date().nullable().optional(),
    dueDateTouched: z.boolean().optional().default(false),
    reportId: z.preprocess(
      (val) => (val === '' || val == null ? null : val),
      z.string().uuid().nullable().optional()
    ),
    assignedUserId: z.preprocess(
      (val) => (val === '' || val == null ? null : val),
      z.string().uuid().nullable().optional()
    ),
  })
  .superRefine((data, ctx) => {
    const ddiType = data.ddiType ?? '';

    if (requiresEntityInquiry(ddiType)) {
      if (!data.entityForInquiry) {
        ctx.addIssue({
          code: 'custom',
          message: 'Entity for Inquiry is required',
          path: ['entityForInquiry'],
        });
      }
    } else if (!data.personForInquiry) {
      ctx.addIssue({
        code: 'custom',
        message: 'Person for Inquiry is required',
        path: ['personForInquiry'],
      });
    }

    if (requiresVin(ddiType)) {
      if (!data.vin) {
        ctx.addIssue({ code: 'custom', message: 'Vehicle Identification Number (VIN) is required', path: ['vin'] });
      } else if (!VIN_REGEX.test(data.vin)) {
        ctx.addIssue({ code: 'custom', message: 'Vehicle Identification Number (VIN) format is invalid', path: ['vin'] });
      }
    } else if (data.vin && !VIN_REGEX.test(data.vin)) {
      ctx.addIssue({ code: 'custom', message: 'Vehicle Identification Number (VIN) format is invalid', path: ['vin'] });
    }

    if (isAddressApplicable(ddiType)) {
      if (requiresAddressType(ddiType) && !data.addressType) {
        ctx.addIssue({ code: 'custom', message: 'Address Type is required', path: ['addressType'] });
      }
      if (!data.addressLine1) {
        ctx.addIssue({ code: 'custom', message: 'Address Line 1 is required', path: ['addressLine1'] });
      }
      if (!data.city) {
        ctx.addIssue({ code: 'custom', message: 'City is required', path: ['city'] });
      }
      if (!data.state) {
        ctx.addIssue({ code: 'custom', message: 'State is required', path: ['state'] });
      }
      if (!data.zipCode) {
        ctx.addIssue({ code: 'custom', message: 'Zip Code is required', path: ['zipCode'] });
      }
    }

    if (data.sameDayRush && data.nextDayRush) {
      ctx.addIssue({
        code: 'custom',
        message: 'Same Day Rush and Next Day Rush cannot both be selected',
        path: ['sameDayRush'],
      });
      ctx.addIssue({
        code: 'custom',
        message: 'Same Day Rush and Next Day Rush cannot both be selected',
        path: ['nextDayRush'],
      });
    }
  });

export type DdiCreateFormValues = z.infer<typeof DdiStep1Schema> & z.infer<typeof DdiStep2Schema>;

const emptyToNull = (value: string | null | undefined): string | null => {
  if (value == null) return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
};

/** Clears inquiry/address/report fields when DDI type changes so stale values are not submitted. */
export const clearSearchDetailsFields = (setValue: UseFormSetValue<DdiCreateFormValues>) => {
  setValue('personForInquiry', null);
  setValue('entityForInquiry', null);
  setValue('vin', null);
  setValue('addressType', null);
  setValue('addressLine1', null);
  setValue('addressLine2', null);
  setValue('city', null);
  setValue('state', null);
  setValue('zipCode', null);
  setValue('showAlternativeAddress', false);
  setValue('alternativeAddressLine1', null);
  setValue('alternativeAddressLine2', null);
  setValue('alternativeAddressCity', null);
  setValue('alternativeAddressState', null);
  setValue('alternativeAddressZipCode', null);
  setValue('reportId', null);
  // Keep assignedUserId — assignment is independent of DDI type / search details.
};

export const applyZodIssuesToForm = (
  issues: z.ZodIssue[],
  setError: (name: keyof DdiCreateFormValues, error: { type: string; message: string }) => void
) => {
  for (const issue of issues) {
    const path = issue.path[0];
    if (typeof path === 'string') {
      setError(path as keyof DdiCreateFormValues, { type: 'manual', message: issue.message });
    }
  }
};

export const buildDdiWorkOrderCreatePayload = (
  values: DdiCreateFormValues
): DdiWorkOrderCreateRequest => {
  const ddiType = values.ddiType;
  const addressTypeForPayload = requiresAddressType(ddiType)
    ? emptyToNull(values.addressType)
    : null;

  const payload: DdiWorkOrderCreateRequest = {
    serviceType: 'Due Diligence',
    ddiType: values.ddiType,
    ddiSubtype: emptyToNull(values.ddiSubtype),
    entryDate: format(values.entryDate, 'yyyy-MM-dd'),
    fileNumber: values.fileNumber.trim(),
    clientCode: values.clientCode.trim(),
    contactId: values.contactId,
    lawyerId: values.lawyerId ?? null,
    courtName: emptyToNull(values.courtName),
    caseType: values.caseType,
    caseSubType: values.caseSubType,
    caseNumber: values.caseNumber.trim(),
    caseName: values.caseName.trim(),
    personForInquiry: emptyToNull(values.personForInquiry),
    entityForInquiry: emptyToNull(values.entityForInquiry),
    vin: emptyToNull(values.vin),
    addressType: addressTypeForPayload,
    addressLine1: emptyToNull(values.addressLine1),
    addressLine2: emptyToNull(values.addressLine2),
    city: emptyToNull(values.city),
    state: emptyToNull(values.state),
    zipCode: emptyToNull(values.zipCode),
    alternativeAddressLine1: values.showAlternativeAddress
      ? emptyToNull(values.alternativeAddressLine1)
      : null,
    alternativeAddressLine2: values.showAlternativeAddress
      ? emptyToNull(values.alternativeAddressLine2)
      : null,
    alternativeAddressCity: values.showAlternativeAddress
      ? emptyToNull(values.alternativeAddressCity)
      : null,
    alternativeAddressState: values.showAlternativeAddress
      ? emptyToNull(values.alternativeAddressState)
      : null,
    alternativeAddressZipCode: values.showAlternativeAddress
      ? emptyToNull(values.alternativeAddressZipCode)
      : null,
    notes: emptyToNull(values.notes),
    sameDayRush: values.sameDayRush ?? false,
    nextDayRush: values.nextDayRush ?? false,
    reportId: values.reportId ?? null,
  };

  // Include dueDate when the user edited it, or when rush drove a same/next-day value so
  // the persisted date matches what the form shows (API also applies rush when omitted).
  const rushSetsDueDate = !!(values.sameDayRush || values.nextDayRush);
  if ((values.dueDateTouched || rushSetsDueDate) && values.dueDate) {
    payload.dueDate = format(values.dueDate, 'yyyy-MM-dd');
  }

  const assignedUserId = emptyToNull(values.assignedUserId ?? null);
  if (assignedUserId) {
    payload.assignedUserId = assignedUserId;
  }

  return payload;
};

const parseDdiDateString = (value: string): Date => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const mapDdiEditResponseToFormValues = (
  data: DdiWorkOrderEditResponse
): DdiCreateFormValues => {
  const hasAltAddress = [
    data.alternativeAddressLine1,
    data.alternativeAddressLine2,
    data.alternativeAddressCity,
    data.alternativeAddressState,
    data.alternativeAddressZipCode,
  ].some((value) => (value ?? '').trim().length > 0);

  return {
    serviceType: 'Due Diligence',
    ddiType: data.ddiType,
    ddiTypeId: null,
    ddiSubtype: data.ddiSubtype,
    ddiSubtypeId: null,
    subtypeRequired: false,
    subtypesLookupPending: false,
    entryDate: parseDdiDateString(data.entryDate),
    fileNumber: data.fileNumber,
    clientId: data.clientId,
    clientCode: data.clientCode,
    clientName: data.clientName,
    contactId: data.contactId,
    contactEmail: data.contactEmail,
    lawyerId: data.lawyerId,
    courtName: data.courtName,
    caseType: data.caseType,
    caseSubType: data.caseSubType,
    caseNumber: data.caseNumber,
    caseName: data.caseName,
    personForInquiry: data.personForInquiry,
    entityForInquiry: data.entityForInquiry,
    vin: data.vin,
    addressType: data.addressType,
    addressLine1: data.addressLine1,
    addressLine2: data.addressLine2,
    city: data.city,
    state: data.state,
    zipCode: data.zipCode,
    showAlternativeAddress: hasAltAddress,
    alternativeAddressLine1: data.alternativeAddressLine1,
    alternativeAddressLine2: data.alternativeAddressLine2,
    alternativeAddressCity: data.alternativeAddressCity,
    alternativeAddressState: data.alternativeAddressState,
    alternativeAddressZipCode: data.alternativeAddressZipCode,
    notes: data.notes,
    sameDayRush: data.sameDayRush,
    nextDayRush: data.nextDayRush,
    dueDate: parseDdiDateString(data.dueDate),
    // false so rush/subtype changes can omit dueDate and allow server recalculation
    dueDateTouched: false,
    reportId: data.reportIds[0] ?? null,
    assignedUserId: data.assignedTo?.userId ?? null,
  };
};

export const buildDdiWorkOrderUpdatePayload = (
  values: DdiCreateFormValues
): DdiWorkOrderUpdateRequest => {
  const payload: DdiWorkOrderUpdateRequest = {
    ddiSubtype: emptyToNull(values.ddiSubtype),
    fileNumber: values.fileNumber.trim(),
    clientCode: values.clientCode.trim(),
    contactId: values.contactId,
    lawyerId: values.lawyerId ?? null,
    courtName: emptyToNull(values.courtName),
    caseType: values.caseType,
    caseSubType: values.caseSubType,
    caseName: values.caseName.trim(),
    personForInquiry: emptyToNull(values.personForInquiry),
    entityForInquiry: emptyToNull(values.entityForInquiry),
    vin: emptyToNull(values.vin),
    addressType: emptyToNull(values.addressType),
    addressLine1: emptyToNull(values.addressLine1),
    addressLine2: emptyToNull(values.addressLine2),
    city: emptyToNull(values.city),
    state: emptyToNull(values.state),
    zipCode: emptyToNull(values.zipCode),
    alternativeAddressLine1: values.showAlternativeAddress
      ? emptyToNull(values.alternativeAddressLine1)
      : null,
    alternativeAddressLine2: values.showAlternativeAddress
      ? emptyToNull(values.alternativeAddressLine2)
      : null,
    alternativeAddressCity: values.showAlternativeAddress
      ? emptyToNull(values.alternativeAddressCity)
      : null,
    alternativeAddressState: values.showAlternativeAddress
      ? emptyToNull(values.alternativeAddressState)
      : null,
    alternativeAddressZipCode: values.showAlternativeAddress
      ? emptyToNull(values.alternativeAddressZipCode)
      : null,
    notes: emptyToNull(values.notes),
    sameDayRush: values.sameDayRush ?? false,
    nextDayRush: values.nextDayRush ?? false,
  };

  if (values.dueDateTouched && values.dueDate) {
    payload.dueDate = format(values.dueDate, 'yyyy-MM-dd');
  }

  // [] clears the report; omit is not used so subtype changes can clear an incompatible report.
  payload.reportIds = values.reportId ? [values.reportId] : [];

  return payload;
};
